import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Link } from 'react-router-dom';

import type { FreeTextbooksSubtopicViewHeaderQuery } from '__free_textbooks_generated__/FreeTextbooksSubtopicViewHeaderQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import ChevronRightIcon from 'ms-components/icons/ChevronRight';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import { FreeTextbooksCTANavbar } from 'ms-pages/FreeTextbooks/FreeTextbooksCTANavbar';
import { DesktopOnly } from 'ms-pages/FreeTextbooks/Layout/Layout';
import {
  getCurriculumSelectorUrl,
  getTextbookSelectorUrl,
} from 'ms-pages/FreeTextbooks/urls';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import { breakPoints, font, fontFamily, fontWeight } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import Separator from 'ms-ui-primitives/Separator';
import { minBreak, multilineTextOverflow } from 'ms-utils/emotion';
import { unwrap } from 'ms-utils/typescript-utils';

const PADDING = 32;
const PADDING_SMALL_SCREENS = 16;

const Wrapper = styled.div({
  fontFamily: fontFamily.body,
  maxWidth: breakPoints.medium,
  margin: 'auto',
  paddingLeft: PADDING_SMALL_SCREENS,
  paddingRight: PADDING_SMALL_SCREENS,

  ...minBreak(breakPoints.small, {
    paddingLeft: PADDING,
    paddingRight: PADDING,
  }),
});

const SubtopicTitle = styled.h1({
  color: colors.cloudBurst,
  ...multilineTextOverflow(2),
  ...font.gilroyHeading,
  ...minBreak(breakPoints.small, font.gilroyLargerHeading),
});

const TitleWrapper = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const TitleDetailsWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'left',
});

const CTAWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  margin: 24,
});

const BREADCRUMB_ITEM_STYLE = {
  color: colors.shuttleGray,
  fontSize: 16,
  fontWeight: fontWeight.semibold,
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

const BreadcrumbItem = styled.div(BREADCRUMB_ITEM_STYLE);

const BreadcrumbLink = styled(Link)(BREADCRUMB_ITEM_STYLE);

const BreadcrumbWrapper = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 13px auto 13px minmax(100px, 1fr)',
  gridColumnGap: 8,
  maxWidth: '100%',
  alignItems: 'center',
});

const RightAligned = styled.div({
  display: 'flex',
  marginLeft: 'auto',
  whiteSpace: 'noWrap',
});

const HeaderContentWrapper = styled.div({
  paddingTop: 32,
  paddingBottom: 32,
  height: 172,
  display: 'flex',
  flexDirection: 'column',
});

const TOPIC_BADGE_SIZE = 70;
const TopicBadge = styled.img({
  height: TOPIC_BADGE_SIZE,
  width: TOPIC_BADGE_SIZE,
});

const FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUERY = graphql`
  query FreeTextbooksSubtopicViewHeaderQuery($subtopicId: ID!) {
    subtopic(id: $subtopicId) {
      id
      title
      topic {
        id
        badgeUrl
        syllabus {
          id
          title
          country {
            name
            code
          }
          curriculum {
            id
            countrySubdivision
          }
          curriculumLevels {
            id
            name
          }
        }
      }
    }
  }
`;

type Props = { subtopicId: string };

export function FreeTextbooksSubtopicViewHeader(props: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeTextbooksSubtopicViewHeader">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeTextbooksSubtopicViewHeaderInner
            {...props}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeTextbooksSubtopicViewHeaderInner({
  subtopicId,
  fetchKey,
}: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<FreeTextbooksSubtopicViewHeaderQuery>(
    FREE_TEXTBOOKS_SUBTOPIC_VIEW_QUERY,
    { subtopicId },
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this after we upgrade relay and have sound types
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  const subtopic = data.subtopic;
  if (subtopic == null) {
    throw new Error('Subtopic not found');
  }

  const { trackStructEvent } = useSnowplow();

  const country = subtopic.topic.syllabus.country;
  const countrySubdivision =
    subtopic.topic.syllabus.curriculum.countrySubdivision;

  return (
    <>
      <DesktopOnly>
        <CTAWrapper>
          <RightAligned>
            <FreeTextbooksCTANavbar context="subtopic_detail" />
          </RightAligned>
        </CTAWrapper>
      </DesktopOnly>
      <Wrapper>
        <HeaderContentWrapper>
          <TitleWrapper>
            <TopicBadge src={subtopic.topic.badgeUrl} alt="topic badge" />
            <Separator size={8} />
            <TitleDetailsWrapper>
              <BreadcrumbWrapper>
                {country != null && (
                  <>
                    <BreadcrumbLink
                      to={getCurriculumSelectorUrl({
                        countryCode: country.code,
                      })}
                      onClick={() => {
                        trackStructEvent({
                          category: 'free_textbook',
                          action: 'clicked_breadcrumb_country',
                          property: 'countryId',
                          label: country.code,
                        });
                      }}
                    >
                      {country.name}
                    </BreadcrumbLink>
                    <ChevronRightIcon />
                  </>
                )}
                {country != null &&
                  countrySubdivision != null &&
                  countrySubdivision !== '' && (
                    <>
                      <BreadcrumbLink
                        to={getTextbookSelectorUrl({
                          countryCode: country.code,
                          curriculumId: subtopic.topic.syllabus.curriculum.id,
                        })}
                        onClick={() => {
                          trackStructEvent({
                            category: 'free_textbook',
                            action: 'clicked_breadcrumb_subdivision',
                            property: 'subdivisionId',
                            label: countrySubdivision,
                          });
                        }}
                      >
                        {countrySubdivision}
                      </BreadcrumbLink>
                      <ChevronRightIcon />
                    </>
                  )}
                <BreadcrumbItem>
                  {subtopic.topic.syllabus.curriculumLevels.length > 0 &&
                    unwrap(subtopic.topic.syllabus.curriculumLevels[0]).name}
                  {subtopic.topic.syllabus.curriculumLevels.length > 1 &&
                    ` - ${
                      unwrap(
                        subtopic.topic.syllabus.curriculumLevels[
                          subtopic.topic.syllabus.curriculumLevels.length - 1
                        ],
                      ).name
                    }`}
                </BreadcrumbItem>
              </BreadcrumbWrapper>
              <Separator size={1} />
              <SubtopicTitle>{subtopic.title}</SubtopicTitle>
            </TitleDetailsWrapper>
          </TitleWrapper>
        </HeaderContentWrapper>
      </Wrapper>
    </>
  );
}
